import { useEffect, useState } from 'react'

import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { Dialog, Icon, Page } from '@loadsmart/miranda-react'

import { resolveAssistantView } from 'assistant/domain/assistantView'
import { newThreadId } from 'assistant/domain/threadId'
import { useSession } from 'auth/ui/hooks/useSession'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'

import AssistantChat from './AssistantChat'
import AssistantLead from './AssistantLead'
import {
  StyledConversationsButton,
  StyledMain,
  StyledShell,
  StyledSidebar,
} from './AssistantPage.styles'
import { AssistantServicesProvider } from './AssistantServices'
import { useAssistantFlag } from './hooks/useAssistantFlag'
import { FeedbackProvider } from './hooks/useFeedback'
import { useThreads } from './hooks/useThreads'
import ThreadList from './ThreadList'

type AssistantConversationProps = {
  threadId: string
  startsExisting: boolean
  onFirstSend: () => void
  onAnswerEnded: (wasExistingThread: boolean) => void
}

/**
 * One conversation. It is mounted again for each thread (see `key` below) and
 * decides once whether the thread already exists on the server. A new thread
 * gets its id in the url on the first send, and must not be re-read then.
 */
function AssistantConversation({
  threadId,
  startsExisting,
  onFirstSend,
  onAnswerEnded,
}: AssistantConversationProps) {
  const [isExistingThread] = useState(startsExisting)

  return (
    <AssistantChat
      threadId={threadId}
      isExistingThread={isExistingThread}
      onSent={() => {
        if (!startsExisting) onFirstSend()
      }}
      onAnswerEnded={() => onAnswerEnded(isExistingThread)}
    />
  )
}

function AssistantWorkspace({ routeThreadId }: { routeThreadId?: string }) {
  const navigate = useNavigate()
  const threads = useThreads()
  const [draftThreadId, setDraftThreadId] = useState(newThreadId)
  const [isListOpen, setIsListOpen] = useState(false)

  const threadId = routeThreadId ?? draftThreadId

  function startNewChat() {
    setDraftThreadId(newThreadId())
    setIsListOpen(false)
    navigate('/assistant')
  }

  function openThread(id: string) {
    setIsListOpen(false)
    navigate(`/assistant/${encodeURIComponent(id)}`)
  }

  function handleFirstSend() {
    navigate(`/assistant/${encodeURIComponent(threadId)}`, { replace: true })
    // The draft is used up. A visit to /assistant must start another one.
    setDraftThreadId(newThreadId())
  }

  // The title is written in the background once the first answer is done. Any
  // later answer only moves its thread up the list.
  function handleAnswerEnded(wasExistingThread: boolean) {
    if (wasExistingThread) {
      void threads.refetch()
    } else {
      threads.refreshTitlesAfterFirstAnswer(threadId)
    }
  }

  const list = (
    <ThreadList
      state={threads}
      activeThreadId={routeThreadId}
      onSelect={openThread}
      onNewChat={startNewChat}
      onDeleted={id => {
        if (id === threadId) startNewChat()
      }}
    />
  )

  return (
    <StyledShell>
      <StyledSidebar>{list}</StyledSidebar>
      <StyledMain>
        <StyledConversationsButton type="button" onClick={() => setIsListOpen(true)}>
          <Icon name="list" size="16px" />
          Conversations
        </StyledConversationsButton>
        <AssistantConversation
          key={threadId}
          threadId={threadId}
          startsExisting={Boolean(routeThreadId)}
          onFirstSend={handleFirstSend}
          onAnswerEnded={handleAnswerEnded}
        />
      </StyledMain>

      {isListOpen && (
        <Dialog open size="small" variant="neutral" onClose={() => setIsListOpen(false)}>
          <Dialog.Header>
            Conversations
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body>{list}</Dialog.Body>
        </Dialog>
      )}
    </StyledShell>
  )
}

export default function AssistantPage() {
  const session = useSession()
  const navigate = useNavigate()
  const flag = useAssistantFlag()
  const { threadId: routeThreadId } = useParams()

  // Same rule as the other preview pages: the chat has no mobile layout yet.
  useEffect(() => {
    if (/Mobi/i.test(window.navigator.userAgent)) {
      navigate('/mobile-not-supported')
    }
  }, [navigate])

  if (flag.isLoading) return null
  if (!flag.isEnabled) return <Navigate to="/" replace />

  const view = resolveAssistantView({
    hasSession: Boolean(session),
    issuer: session?.user.issuer,
  })

  return (
    <AppLayout>
      <Header unauthCtaLabel="Sign in" unauthCtaHref="/" />
      <Page.Content>
        <Page.Body>
          {view === 'chat' ? (
            <AssistantServicesProvider>
              <FeedbackProvider>
                <AssistantWorkspace routeThreadId={routeThreadId} />
              </FeedbackProvider>
            </AssistantServicesProvider>
          ) : (
            <AssistantLead />
          )}
        </Page.Body>
      </Page.Content>
    </AppLayout>
  )
}
